import { Component, OnInit, ViewChild } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { Router } from "@angular/router";
import { FormControl, FormGroup } from "@angular/forms";
import { validColorValidator } from "ngx-colors";
import { UserAuthService } from "@career/core/services/user-auth.service";
import { take, concatMap, map, debounceTime, switchMap } from "rxjs/operators";
import { Subject, Subscription } from "rxjs";
import {
  PageConfigTypes,
  SelectOption,
  Theme,
} from "../models/page-config.model";
import { EditConfigurationsComponent } from "../Shared/components/edit-configurations/edit-configurations.component";
import { PortalService } from "@career/core/services/portal.service";
import { ThemingService } from "@career/core/services/theming.service";
import { FontFamilyConfig, Translations } from "../configs/page-config.config";
import { UserService } from "@career/core/services/user.service";
import { ConfirmDialogComponent } from "../Shared/components/confirm-dialog/confirm-dialog.component";
import { TemplateConfiguration } from "@career/core/models/component.model";
import { ConfigurableComponent } from "../Shared/components/configurable.component";
import { CoreAction } from "@career/core/store/actions/core.actions";
import { Store } from "@ngxs/store";
import { LayerService } from "kleos-ui";
import { DomSanitizer } from "@angular/platform-browser";
import { MatDrawer } from "@angular/material/sidenav";

@Component({
  selector: "app-page-configuration",
  templateUrl: "./page-configuration.component.html",
  styleUrls: ["./page-configuration.component.scss"],
})
export class PageConfigurationComponent
  extends ConfigurableComponent
  implements OnInit
{
  userName: any;
  pageConfigTypes = PageConfigTypes;
  subscriptions: Subscription[] = [];
  translations = Translations;
  templateConfig: SelectOption[] = [];
  fontFamilyConfig = FontFamilyConfig;
  selectedTemplate: string;
  selectedFont: string;
  config: TemplateConfiguration;
  srcUrl: any;
  isPreviewMode$ = this.portalSvc.isPreviewMode();
  customColorKeys: Array<"primaryColor" | "accentColor" | "warnColor"> = [
    "primaryColor",
    "accentColor",
    "warnColor",
  ];
  customColorLabels: { [k in "primaryColor" | "accentColor" | "warnColor"]: string } = {
    primaryColor: "Primary Color",
    accentColor: "Accent Color",
    warnColor: "Warn Color",
  };
  customColorsForm = new FormGroup({
    primaryColor: new FormControl("", validColorValidator()),
    accentColor: new FormControl("", validColorValidator()),
    warnColor: new FormControl("", validColorValidator()),
  });
  @ViewChild('drawer', { static: true }) public drawer!: MatDrawer;
  private customColorsSave$ = new Subject<{
    primaryColor: string;
    accentColor: string;
    warnColor: string;
  }>();


  constructor(
    private router: Router,
    private userAuthService: UserAuthService,
    private portalSvc: PortalService,
    private themingSvc: ThemingService,
    layerService: LayerService,
    private userSvc: UserService,
    private dialog: MatDialog
  ) {
    super(layerService);
  }

  onConfigurationSave(data: TemplateConfiguration): void {}

  ngOnInit(): void {
    this.getTemplatesList();
    this.getPortalObject();
    this.userSvc
      .getUser()
      .pipe(take(1))
      .subscribe((user) => {
        this.userName = user?.name;
      });

    this.customColorKeys.forEach((key) => {
      const sub = this.customColorsForm.controls[key].valueChanges.subscribe(
        () => this.onCustomColorChange()
      );
      this.subscriptions.push(sub);
    });

    // Serialize color saves: debounce rapid picker changes and cancel any
    // in-flight PATCH so concurrent writes on the same portal document don't
    // cause a Mongo WriteConflict.
    const colorSaveSub = this.customColorsSave$
      .pipe(
        debounceTime(300),
        switchMap((colors) =>
          this.portalSvc.updateConfigurations({ colors }).pipe(
            concatMap((configRes) =>
              this.portalSvc.initializeApp(configRes, true, false)
            )
          )
        )
      )
      .subscribe();
    this.subscriptions.push(colorSaveSub);

      this.drawer.close();
  }

  closeLayer() {
    this.layerService.close();
    return true;
  }

  getTemplatesList(): void {
    const sub = this.portalSvc.getTemplatesList().subscribe((res) => {
      this.templateConfig = this.getOptionsArray(res.portaltemplates, true);
    });
    this.subscriptions.push(sub);
  }

  getOptionsArray(list: Theme[], isTemplateList = false): SelectOption[] {
    return list.map((listItem) => {
      return {
        name: listItem.name,
        value: isTemplateList ? listItem.value : listItem.name,
        id: listItem._id,
      };
    });
  }

  getPortalObject(): void {
    this.portalSvc.getPortalObj().subscribe((portal) => {
      this.selectedTemplate = portal.template._id;
      this.selectedFont = portal.fontFamily;
      this.config = portal.configuration;
      const colors = portal.colors;
      this.customColorsForm.patchValue(
        {
          primaryColor: colors?.primaryColor,
          accentColor: colors?.accentColor,
          warnColor: colors?.warnColor,
        },
        { emitEvent: false }
      );
    });
  }

  onCustomColorChange(): void {
    if (this.customColorsForm.invalid) {
      return;
    }
    const colors = {
      primaryColor: this.customColorsForm.controls.primaryColor.value,
      accentColor: this.customColorsForm.controls.accentColor.value,
      warnColor: this.customColorsForm.controls.warnColor.value,
    };
    // Live preview immediately; persist (debounced/serialized) off the same
    // committed form value so we never save a stale/empty color.
    this.themingSvc.currentTheme.next(colors);
    this.customColorsSave$.next(colors);
  }

  onTemplateChange(): void {
    this.updateConfiguration({
      template: { _id: this.selectedTemplate },
    });
  }

  onFontChange(): void {
    this.updateConfiguration({ fontFamily: this.selectedFont });
  }

  updateConfiguration(payload: any): void {
    const sub = this.portalSvc
      .updateConfigurations(payload)
      .pipe(
        concatMap((configRes) => {
          return this.portalSvc.initializeApp(configRes, true, false);
        })
      )
      .subscribe();
    this.subscriptions.push(sub);
  }

  onPublish(): void {
    this.dialog.open(ConfirmDialogComponent, {
      minWidth: "500px",
      maxWidth: "600px",
      disableClose: true,
    });
  }

  onPreview(): void {
    this.portalSvc.togglePreviewMode();
    if(this.config.enableHomePage){
      this.router.navigate(['/']);
    } else {
      this.router.navigate(['/dashboard'])
    }
  }

  getConfigurationType(): keyof typeof PageConfigTypes {
    return PageConfigTypes.GLOBAL;
  }

  getTemplateConfiguration(): TemplateConfiguration {
    return { ...this.config };
  }

  signout() {
    this.userAuthService.logout(true).subscribe(() => {
      this.router.navigate(["/admin/login"]);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((e) => e.unsubscribe());
  }
}
